// 2026-07-02 US-003: pure client-side channel projection over recipients returned by
// the real GetBulkNotificationRecipients API. Audience resolution (container/reference
// matching, all-or-nothing validation) now happens server-side — see
// BulkNotificationService.GetRecipients. This module only decides, per recipient,
// which channel applies given the selected preferred channel + fallback setting.
import { Constants } from 'common/constants';
import { NotifChannel, RecipientApiResult, RecipientRow, ResolvedChannel } from 'common/models/BulkNotificationTypes';

// Mirrors AssignBarcodes.parseBarcodes: split on comma/newline, trim, drop empties, dedupe.
export const parseRefs = (text: string): string[] => {
  const refs = (text || '')
    .split(/[,\n]/)
    .map((r) => r.trim())
    .filter((r) => r.length > 0);
  return Array.from(new Set(refs));
};

const isValidEmail = (email: string | null): boolean => !!email && Constants.EMAIL_REGEX_INPUT.test(email.trim());

const isValidMobile = (mobile: string | null): boolean => !!mobile && /\d/.test(mobile.trim());

// Bidirectional: preferred channel -> the other channel (when fallback on) -> unreachable.
const projectChannel = (row: RecipientApiResult, channel: NotifChannel, fallbackOn: boolean): ResolvedChannel => {
  if (channel === NotifChannel.Email) {
    if (isValidEmail(row.Email)) return ResolvedChannel.Email;
    if (fallbackOn && isValidMobile(row.Mobile)) return ResolvedChannel.SmsFallback;
    return ResolvedChannel.Unreachable;
  }
  if (isValidMobile(row.Mobile)) return ResolvedChannel.Sms;
  if (fallbackOn && isValidEmail(row.Email)) return ResolvedChannel.EmailFallback;
  return ResolvedChannel.Unreachable;
};

export interface ProjectRecipientsResult {
  Rows: RecipientRow[];
  ReachableCount: number;
  Unreachable: RecipientRow[];
}

export const projectRecipients = (
  recipients: RecipientApiResult[],
  channel: NotifChannel,
  fallbackOn: boolean
): ProjectRecipientsResult => {
  const rows: RecipientRow[] = (recipients || []).map((r) => ({
    RefNo: r.RefNo,
    CustomerName: r.CustomerName,
    Resolved: projectChannel(r, channel, fallbackOn),
    Email: r.Email,
    Mobile: r.Mobile,
  }));

  const unreachable = rows.filter((r) => r.Resolved === ResolvedChannel.Unreachable);
  return {
    Rows: rows,
    ReachableCount: rows.length - unreachable.length,
    Unreachable: unreachable,
  };
};

// 2026-07-02 US-003: preview-only mirror of the server's dedupe-by-contact rule (see
// BulkNotificationService.Send) — several RefNo rows sharing one email/mobile collapse to
// one actual message. Used so the Confirm modal shows the real number of messages that
// will go out, not the raw reachable-row count (the two can differ once one customer's
// multiple boxes are in the audience).
export const countDedupedContacts = (rows: RecipientRow[]): number => {
  const keys = new Set<string>();
  rows.forEach((r) => {
    if (r.Resolved === ResolvedChannel.Unreachable) return;
    const usesEmail = r.Resolved === ResolvedChannel.Email || r.Resolved === ResolvedChannel.EmailFallback;
    const contact = (usesEmail ? r.Email : r.Mobile) || '';
    keys.add(`${usesEmail ? 'Email' : 'SMS'}:${contact.trim().toLowerCase()}`);
  });
  return keys.size;
};
